import { mutationTypes, actionTypes, errorTypes } from '../../../types';
import { logger, errorHandler } from '../../../tools';
import { locationEnable } from '../../../tools/JS2NativeInterface';

// Regard markers as point with 7 meter radius.
const defaultMarkerRadius = 7;
// Options for HTML5 geo location API
const geoLocationOptions = {
	enableHighAccuracy: true,
	// timeout: 30000,
	// maximumAge: 0,
	timeout: 5000,
	maximumAge: 15000,
};
let geoLocationWatcher;

export default {
	state: {
		currentCoordinate: {
			latitude: null,
			longitude: null,
			altitude: null,
			heading: null,
			accuracy: null,
		},
		permissionRequested: false,
		geoLocationInitializing: false,
		geoLocationInitialized: false,
		currentLocations: [],
		combineLocations: true,
	},
	mutations: {
		[mutationTypes.getLocationSetHeading](state, { heading }) {
			state.heading = heading;
		},
		[mutationTypes.geoLocationSetCoordinates](state, { currentCoordinate, currentLocations }) {
			state.currentCoordinate = currentCoordinate;
			state.currentLocations = currentLocations;
			state.geoLocationInitializing = false;
			state.geoLocationInitialized = true;
		},
		[mutationTypes.geoLocationInitialize](state, { combineLocations }) {
			state.geoLocationInitializing = true;
			state.combineLocations = combineLocations;
		},
		[mutationTypes.geoLocationStop](state) {
			state.geoLocationInitialized = false;
			state.geoLocationInitializing = false;
		},
		[mutationTypes.geoLocationRequestedPermission](state) {
			state.permissionRequested = true;
		},
	},
	actions: {
		[actionTypes.geoLocationStart]({ commit, rootState, state }) {
			if (rootState.isPreview) return;

			if (state.geoLocationInitializing || state.geoLocationInitialized) return;

			// Initialize the location look up objects
			// const { exhibitions } = rootState.dataSourceCFG;
			const { combineLocations } = rootState.dataSourceCFG.configWebClient.locations;
			commit(mutationTypes.geoLocationInitialize, { combineLocations });
		},
		/**
		 *  this action reuqest device Positon from App or Browser this can result in a user dialog,
		 *  so this will only triggered once, second time this action called during runtime it will not be triggered
		 * @param {*} rootState
		 * @param {*} dispatch
		 */
		async [actionTypes.geoLocationRequestPermission]({ rootState, dispatch, commit, state }) {
			logger.log(
				`geoLocation: Start geoLocation Interface! isApp: ${
					rootState.isApp
				} , permission allready requested ${state.permissionRequested}`
			);
			// TODO: check if it is an indoor or an outdoor map

			if (!state.permissionRequested) {
				if (rootState.isApp) {
					logger.log('geoLocation: App app uses native interface.');
					locationEnable();
				} else if (window.navigator.geolocation) {
					logger.log('geoLocation: uses browser interface');
					dispatch(actionTypes.geoLocationWatch);
				}
				commit(mutationTypes.geoLocationRequestedPermission);
			} else {
				logger.log('geoLocation: Permission allready requested');
			}
		},
		async [actionTypes.geoLocationWatch]({ dispatch }) {
			// Initialize the browser geo location API and bind the watch functions
			// that updates the geo location.
			logger.log('geoLocation: Geo use webinterface.');
			geoLocationWatcher = window.navigator.geolocation.watchPosition(
				position => {
					dispatch(actionTypes.geoLocationChanged, {
						latitude: position.coords.latitude,
						longitude: position.coords.longitude,
						altitude: position.coords.altitude,
						heading: position.coords.heading,
						accuracy: position.coords.accuracy,
					});
				},
				// Handle errors from the watch function.
				error => {
					if (error.code === error.PERMISSION_DENIED) {
						errorHandler(new Error(errorTypes.geoPositionPermissionDenied));
					} else if ([error.POSITION_UNAVAILABLE, error.UNKNOWN_ERROR].includes(error.code)) {
						errorHandler(new Error(errorTypes.geoPositionPositionUnavailable));
					}
					// else if ([error.TIMEOUT, error.UNKNOWN_ERROR].includes(error.code)) {}
					logger.warn(`GeoLocation ERROR(${error.code}): ${error.message}`);
				},
				geoLocationOptions
			);
		},
		// Handle a location change, find all matching locations and commit the results
		// to the state.
		async [actionTypes.geoLocationChanged]({ state, rootState, commit }, currentCoordinate) {
			const geolib = await import(/* webpackChunkName: "geolib" */ 'geolib');
			const currentLocations = rootState.dataSourceCFG.locations.filter(location => {
				if (
					!(
						location.type === 'geoLocation' &&
						location.geoLocation.shape === 'marker' &&
						(state.combineLocations || location.exhibitions.has(rootState.currentExhibitionID))
					)
				) {
					return false;
				}
				const [_point] = location.geoLocation.coordinates;
				const point = Array.isArray(_point)
					? { latitude: _point[1], longitude: _point[0] }
					: { latitude: _point.lat, longitude: _point.lng };
				return geolib.isPointInCircle(
					currentCoordinate,
					point,
					defaultMarkerRadius + Math.min(currentCoordinate.accuracy / 2, 10)
				);
			});
			commit(mutationTypes.geoLocationSetCoordinates, { currentCoordinate, currentLocations });
		},
		// Stop using the browser location API
		[actionTypes.getLocationStop]() {
			navigator.geolocation.clearWatch(geoLocationWatcher);
			commit(mutationTypes.geoLocationStop);
		},
	},
	plugins: [
		store => {
			/**
			"enabled" (user enabled GPS)
			"denied" (user did not want GPS)
			"agreed" (user agreed to enable GPS, but the actual outcome is unsure)
			"already-on" (GPS was already on)
			"not-available" (GPS is not available)
			"hardware-error" (user tried to enable GPS, but there was hardware error)
			**/
			window.nativeOnLocationEnable = result => {
				logger.log(`geoLocation nativeOnLocationEnable: result: ${result}`);
				if (result === 'enabled') {
					store.dispatch(actionTypes.geoLocationWatch);
				} else if (['denied', 'not-available', 'hardware-error'].includes(result)) {
					store.commit(mutationTypes.geoLocationStop);
					if (result === 'denied') {
						errorHandler(new Error(errorTypes.geoPositionPermissionDenied));
					} else if (['not-available', 'hardware-error'].includes(result)) {
						errorHandler(new Error(errorTypes.geoPositionPositionUnavailable));
					}
				}
			};
			window.nativeOnLocationUpdated = (lat, lon, accuracyInMeters, altitudeInMeters) => {
				logger.log(`GeoLocation changed ${lat}, ${lon} ${accuracyInMeters}`);
				store.dispatch(actionTypes.geoLocationChanged, {
					latitude: lat,
					longitude: lon,
					accuracy: accuracyInMeters,
					altitude: altitudeInMeters,
				});
			};
			window.nativeOnHeadingUpdated = (degreesRelativetoGeographicNorth, accuracyInDegrees) => {
				store.commit(mutationTypes.getLocationSetHeading, {
					heading: degreesRelativetoGeographicNorth,
					accuracy: accuracyInDegrees,
				});
			};
		},
	],
};
